// 数据格式化工具类
// 支持货币、数字、百分比、日期等多种数据类型的格式化
import { FieldConfig } from '../types/field-mapping';

export class DataFormatter {
  /**
   * 格式化数值
   */
  static formatNumber(value: number | null | undefined, precision: number = 0): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return value.toLocaleString('zh-CN', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    });
  }

  /**
   * 格式化货币
   */
  static formatCurrency(value: number | null | undefined, precision: number = 0): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return `¥${value.toLocaleString('zh-CN', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    })}`;
  }

  /**
   * 格式化百分比
   */
  static formatPercentage(value: number | null | undefined, precision: number = 2): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return `${value.toFixed(precision)}%`;
  }

  /**
   * 格式化日期
   */
  static formatDate(value: string | Date | null | undefined, format: string = 'YYYY-MM-DD'): string {
    if (!value) return '-';

    const date = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(date.getTime())) return '-';

    switch (format) {
      case 'YYYY.MM':
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      case 'datetime':
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      case 'date':
        return date.toLocaleDateString('zh-CN');
      case 'time':
        return date.toLocaleTimeString('zh-CN');
      default:
        return date.toLocaleDateString('zh-CN');
    }
  }

  /**
   * 格式化布尔值
   */
  static formatBoolean(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return value ? '是' : '否';
  }

  /**
   * 根据字段配置格式化值
   */
  static formatValue(value: any, fieldConfig: FieldConfig): string {
    if (value === null || value === undefined) {
      return '-';
    }

    let formattedValue: string;

    switch (fieldConfig.type) {
      case 'number':
        formattedValue = this.formatNumber(value, fieldConfig.precision);
        break;
      case 'currency':
        formattedValue = this.formatCurrency(value, fieldConfig.precision);
        break;
      case 'percentage':
        formattedValue = this.formatPercentage(value, fieldConfig.precision);
        break;
      case 'date':
      case 'datetime':
        formattedValue = this.formatDate(value, fieldConfig.format);
        break;
      case 'boolean':
        formattedValue = this.formatBoolean(value);
        break;
      case 'select':
      case 'multiselect':
        if (fieldConfig.options) {
          const option = fieldConfig.options.find(opt => opt.value === value);
          formattedValue = option ? option.label : String(value);
        } else {
          formattedValue = String(value);
        }
        break;
      default:
        formattedValue = String(value);
    }

    // 添加单位
    if (fieldConfig.unit && formattedValue !== '-') {
      formattedValue = `${formattedValue} ${fieldConfig.unit}`;
    }

    return formattedValue;
  }

  /**
   * 添加单位
   */
  static addUnit(formattedValue: string, unit?: string): string {
    if (!unit || formattedValue === '-') {
      return formattedValue;
    }
    return `${formattedValue} ${unit}`;
  }

  /**
   * 格式化文件大小
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 格式化执行时间
   */
  static formatExecutionTime(milliseconds: number): string {
    if (milliseconds < 1000) {
      return `${milliseconds}ms`;
    } else if (milliseconds < 60000) {
      return `${(milliseconds / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(milliseconds / 60000);
      const seconds = ((milliseconds % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * 格式化数字范围
   */
  static formatRange(min: number, max: number, precision: number = 2): string {
    const formattedMin = this.formatNumber(min, precision);
    const formattedMax = this.formatNumber(max, precision);
    return `${formattedMin} - ${formattedMax}`;
  }

  /**
   * 格式化货币范围
   */
  static formatCurrencyRange(min: number, max: number, precision: number = 2): string {
    const formattedMin = this.formatCurrency(min, precision);
    const formattedMax = this.formatCurrency(max, precision);
    return `${formattedMin} - ${formattedMax}`;
  }

  /**
   * 解析格式化的数值
   */
  static parseNumber(formattedValue: string): number | null {
    if (!formattedValue || formattedValue === '-') {
      return null;
    }

    // 移除千分位分隔符和货币符号
    const cleanValue = formattedValue
      .replace(/[¥,\s]/g, '')
      .replace(/%$/, '');

    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * 格式化表格单元格值（带颜色）
   */
  static formatCellValue(value: any, fieldConfig: FieldConfig): {
    text: string;
    color?: string;
    backgroundColor?: string;
  } {
    const text = this.formatValue(value, fieldConfig);
    
    // 根据字段类型和值设置颜色
    let color: string | undefined;
    let backgroundColor: string | undefined;

    if (fieldConfig.type === 'currency' && fieldConfig.key === 'profitPerTon') {
      // 利润字段：正数绿色，负数红色
      if (value > 0) {
        color = '#52c41a';
      } else if (value < 0) {
        color = '#f5222d';
      }
    } else if (fieldConfig.type === 'percentage') {
      // 百分比字段：根据范围设置颜色
      if (value >= 90) {
        color = '#52c41a'; // 绿色
      } else if (value >= 70) {
        color = '#faad14'; // 橙色
      } else if (value < 50) {
        color = '#f5222d'; // 红色
      }
    }

    return { text, color, backgroundColor };
  }

  /**
   * 格式化导出数据
   */
  static formatForExport(value: any, fieldConfig: FieldConfig): string {
    if (value === null || value === undefined) {
      return '';
    }

    // 导出时不添加货币符号和单位，保持原始数值
    switch (fieldConfig.type) {
      case 'number':
      case 'currency':
        return typeof value === 'number' ? value.toString() : String(value);
      case 'percentage':
        return typeof value === 'number' ? value.toString() : String(value);
      case 'date':
      case 'datetime':
        return this.formatDate(value, 'YYYY-MM-DD HH:mm:ss');
      case 'boolean':
        return value ? '是' : '否';
      default:
        return String(value);
    }
  }

  /**
   * 批量格式化数据行
   */
  static formatDataRow(row: any, fieldConfigs: FieldConfig[]): any {
    const formattedRow: any = {};
    
    fieldConfigs.forEach(config => {
      const value = row[config.key];
      formattedRow[config.key] = this.formatValue(value, config);
    });

    return formattedRow;
  }

  /**
   * 格式化搜索高亮
   */
  static highlightSearchText(text: string, searchText: string): string {
    if (!searchText || !text) {
      return text;
    }

    const regex = new RegExp(`(${searchText})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }
}
